import { faChevronDown, faChevronUp, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, Card, Row, Table } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FormattedMessage, injectIntl } from "react-intl";
import { sanitizeNoAllowedTags } from "../../../utils/ObjectUtils";
import { QuestionIcon } from "../../../utils/QuestionUtils";
import IconTooltip from "../../common/IconTooltip";

class SurveyVersionComparisonTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      collapsedRowsAndColumns: {},
      collapsedChoices: {},
      focusedId: null
    };
  }

  handleToggleRowsAndColumns(key) {
    this.setState((state) => ({
      collapsedRowsAndColumns: {
        ...state.collapsedRowsAndColumns,
        [key]: !state.collapsedRowsAndColumns?.[key]
      }
    }));
  }

  handleToggleChoices(key) {
    this.setState((state) => ({
      collapsedChoices: {
        ...state.collapsedChoices,
        [key]: !state.collapsedChoices?.[key]
      }
    }));
  }

  generateTooltip(text) {
    return <Tooltip>{text}</Tooltip>;
  }

  render() {
    const { surveyComparison, latestSurvey, lastApprovedSurvey, intl, currentIdentifier, currentVersion } = this.props;
    let tableRows = [];
    const latestSurveyIdentifier = latestSurvey?.identifier ?? latestSurvey?.surveyId;
    const isCurrentSurveySelected =
      latestSurveyIdentifier === currentIdentifier && latestSurvey?.version === currentVersion;

    this.rowKeyCounts = {};
    this.renderDiffTableContent(surveyComparison, tableRows);

    return (
      <Table responsive striped hover>
        <thead>
          <tr>
            <td className={"thick-border-right background-grey text-centered title"} colSpan={2}>
              {this.getSurveyHeadline(lastApprovedSurvey)}
            </td>
            <td className={"thick-border-right background-grey text-centered title"} colSpan={2}>
              {`${this.getSurveyHeadline(latestSurvey)} ${isCurrentSurveySelected ? `(${intl.formatMessage({ id: "survey.version.compare.current" })})` : ""}`}
            </td>
            <td className={"thick-border-bottom background-grey title"}>
              {intl.formatMessage({ id: "survey.version.compare.change" })}
            </td>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => {
            return row;
          })}
        </tbody>
      </Table>
    );
  }

  duplicateKeysWarningIcon(tooltipMessageId = "survey.version.compare.warning.duplicate.keys") {
    return (
      <IconTooltip 
        iconClass="ms-2 text-danger" 
        iconDefinition={faExclamationTriangle} 
        tooltipMessageId={tooltipMessageId}
      />
    );
  } 

  getSurveyHeadline(survey) {
    if (!survey) {
      return "";
    }

    const surveyName = survey.surveyName ?? survey.name ?? "";
    const surveyVersion = survey.version ?? survey?.surveyTemplate?.version ?? "";

    return `${surveyName} - Version ${surveyVersion}`;
  }

  renderPage(page, rows) {
    const { editPage, isLastVersionSelected, isPageExpanded, onTogglePage } = this.props;

    const typeIndex = page.typeIndex;
    const pageExpanded = !isPageExpanded || isPageExpanded(page);
    const cellBorder = "border-bottom border-top";
    const cellClass = `${cellBorder} cell-${page.change[0] ? page.change[0] : "no-change"}`;
    const newElem = page.change[0] && page.change[0] === "new";
    const deletedElem = page.change[0] && page.change[0] === "deleted";
    const pageKey = this.getPageToggleIdentifier(page);

    rows.push(
      page.emptyKey ? (
        <tr key={this.getUniqueRowIdentifier(page)}>
          <td className={`${cellClass} thick-border-right`}></td>
          <td className={`${cellClass} thick-border-right`}></td>
          <td className={cellClass}></td>
        </tr>
      ) : (
        <tr key={this.getUniqueRowIdentifier(page)}>
          {newElem ? (
            <td colSpan={2} className={`${cellBorder} cell-new thick-border-right`}></td>
          ) : (
            <>
              <td className={typeIndex === 0 ? cellClass : ""}>
                {typeIndex === 0 && (
                  <Button
                    variant="link"
                    className="p-0 text-start"
                    onClick={() => onTogglePage && onTogglePage(page)}
                  >
                    <FontAwesomeIcon icon={pageExpanded ? faChevronUp : faChevronDown} className="me-2" />
                    {page.titleOld || page.nameOld}
                  </Button>
                )}
              </td>
              <td className={`${cellClass} thick-border-right text-end`}>
                {!page.change ||
                page.change.length === 0 ||
                (page.change && page.change.length > 0 && page.change[0] !== "new")
                  ? page.keyOld
                  : ""}
              </td>
            </>
          )}

          {deletedElem ? (
            <td colSpan={2} className={`${cellBorder} cell-deleted thick-border-right`}></td>
          ) : (
            <>
              <td className={typeIndex === 0 ? cellClass : ""}>
                {typeIndex === 0 && (
                  <Button
                    variant="link"
                    className="p-0 text-start"
                    onClick={() => onTogglePage && onTogglePage(page)}
                  >
                    <FontAwesomeIcon icon={pageExpanded ? faChevronUp : faChevronDown} className="me-2" />
                    {page.titleNew || page.nameNew}
                  </Button>
                )}
              </td>
              <td className={`${cellClass} thick-border-right`}>
                {!page.change ||
                page.change.length === 0 ||
                (page.change && page.change.length > 0 && page.change[0] !== "deleted") ? (
                  <>
                    {!isLastVersionSelected ?
                      (
                        <>
                          {page.keyNew}
                        </>
                      ) : (
                        <input
                          type="text"
                          value={page.keyNew}
                          autoFocus={pageKey === this.state.focusedId ? true : false}
                          onFocus={(e) => {
                            if (this.state.focusedId !== pageKey) {
                              this.setState({ focusedId: pageKey });
                            }
                          }}
                          onChange={(e) => {
                            this.setState({ focusedId: pageKey });
                            editPage(
                              page.entity.name,
                              page.entity.title,
                              e.target.value,
                              page.entity.description,
                              page.entity.visible
                            );
                          }}
                        />
                      )
                    }

                    {page.newKeyAlreadyUsed &&
                      this.duplicateKeysWarningIcon("survey.version.compare.warning.duplicate.page.keys")}
                  </>
                ) : (
                  ""
                )}
              </td>
            </>
          )}

          <td className={cellClass}>
            {page.change.length > 0 && <FormattedMessage id={`survey.version.compare.change.${page.change[0]}`} />}
          </td>
        </tr>
      )
    );

    if (pageExpanded && page.children) {
      page.children.forEach((question) => this.renderQuestion(question, rows));
    }
  }

  renderQuestion(question, rows) {
    const { changeQuestionField, isLastVersionSelected, isQuestionExpanded, onToggleQuestion } = this.props;

    const cellType = `cell-${question.change[0] ? question.change[0] : "no-change"}`;
    const cellTypeForAccordion = `${cellType} d-block`
    const cellClass = `border-bottom ${cellType}`;
    const newElem = question.change[0] && question.change[0] === "new";
    const deletedElem = question.change[0] && question.change[0] === "deleted";
    const questionKey = this.getQuestionToggleIdentifier(question);
    const questionExpanded = !isQuestionExpanded || isQuestionExpanded(question);

    rows.push(
      <tr key={this.getUniqueRowIdentifier(question)}>
        <td colSpan={4} className={"thick-border-right"}>
          <Card>
            <Row>
              <div className={"question"}>
                {!newElem && (
                  <Button
                    as={Card.Header}
                    variant="link"
                    onClick={() => onToggleQuestion && onToggleQuestion(question)}
                    className={cellTypeForAccordion}
                  >
                    <i className="question-type-icon">
                      <QuestionIcon type={question.dataTypeOld.charAt(0).toLowerCase() + question.dataTypeOld.slice(1)} />
                    </i>
                    <Row className="g-1 align-items-center">
                      {question.children && question.children.length > 0 && (
                        <Col xs="auto" className="pe-1">
                          <FontAwesomeIcon icon={questionExpanded ? faChevronUp : faChevronDown} />
                        </Col>
                      )}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={this.generateTooltip(
                          sanitizeNoAllowedTags(question.titleOld) || sanitizeNoAllowedTags(question.nameOld)
                        )}
                      >
                        <Col className="text-ellipsis text-start ps-1">
                          {sanitizeNoAllowedTags(question.titleOld) || sanitizeNoAllowedTags(question.nameOld)}
                        </Col>
                      </OverlayTrigger>

                      <Col lg={question.children && question.children.length > 0 ? 4 : 5} className="ps-2 text-end">
                        {question.keyOld} 
                        {question.oldKeyAlreadyUsed && this.duplicateKeysWarningIcon()}
                      </Col>
                    </Row>
                  </Button>
                )}
              </div>
              <div className={"question"}>
                {!deletedElem && (
                  <Button
                    as={Card.Header}
                    variant="link"
                    onClick={() => onToggleQuestion && onToggleQuestion(question)}
                    className={cellTypeForAccordion}
                  >
                    <i className="question-type-icon">
                      <QuestionIcon type={question.dataTypeNew.charAt(0).toLowerCase() + question.dataTypeNew.slice(1)} />
                    </i>
                    <Row className="g-1 align-items-center">
                      {question.children && question.children.length > 0 && (
                        <Col xs="auto" className="pe-1">
                          <FontAwesomeIcon icon={questionExpanded ? faChevronUp : faChevronDown} />
                        </Col>
                      )}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={this.generateTooltip(
                          sanitizeNoAllowedTags(question.titleNew) || sanitizeNoAllowedTags(question.nameNew)
                        )}
                      >
                        <Col className="text-ellipsis text-start ps-1">
                          {sanitizeNoAllowedTags(question.titleNew) || sanitizeNoAllowedTags(question.nameNew)}
                        </Col>
                      </OverlayTrigger>

                      <Col lg={question.children && question.children.length > 0 ? 4 : 5} className="ps-2 text-end">
                        {!question.change ||
                        question.change.length === 0 ||
                        (question.change && question.change.length > 0 && question.change[0] !== "deleted") ? (
                          <>
                            {!isLastVersionSelected ?
                              (
                                <>
                                  {question.keyNew}
                                </>
                              ) : (
                                <input
                                  type="text"
                                  value={question.keyNew}
                                  autoFocus={questionKey === this.state.focusedId ? true : false}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onFocus={(e) => {
                                    if (this.state.focusedId !== questionKey) {
                                      this.setState({ focusedId: questionKey });
                                    }
                                  }}
                                  onChange={(e) => {
                                    this.setState({ focusedId: questionKey });
                                    changeQuestionField(
                                      question.pageName, 
                                      question.questionName, 
                                      "key", 
                                      e.target.value, 
                                      null, 
                                      null, 
                                      null, 
                                      true);
                                  }}
                                />
                              )
                            }

                            {question.newKeyAlreadyUsed && this.duplicateKeysWarningIcon()}
                          </>
                        ) : (
                          ""
                        )}
                      </Col>  

                    </Row>
                  </Button>
                )}
              </div>
            </Row>
            {questionExpanded && question.children && (
              <Card.Body>
                {this.renderRowsAndColumns(question.children, question.change)}
              </Card.Body>
            )}
          </Card>
        </td>
        <td className={cellClass}>
          {question.change.length > 0 && (
            <FormattedMessage id={`survey.version.compare.change.${question.change[0]}`} />
          )}
        </td>
      </tr>
    );
  }

  renderRowsAndColumns(elements, questionChanges) {
    const { changeQuestionField, intl, isLastVersionSelected } = this.props;

    let rows = [];
    let activeGroupKey = null;
    let activeGroupCollapsed = false;

    elements.forEach((element) => {
      const cellType = `cell-${element.change[0] ? element.change[0] : "no-change"}`;
      const elementKey = this.getElementToggleIdentifier(element);
      const choicesGroupKey = this.getChoicesGroupIdentifier(element);
      const choicesCollapsed = !!this.state.collapsedChoices?.[choicesGroupKey];
      const hasChoices =
        element.children &&
        element.dataTypeOld !== "predefinedList" &&
        element.dataTypeNew !== "predefinedList";
      const hasCollapsibleChoices = hasChoices && this.hasCollapsibleChoices(element);

      if (element.emptyKey) {
        activeGroupKey = this.getRowsAndColumnsGroupIdentifier(element);
        activeGroupCollapsed = !!this.state.collapsedRowsAndColumns?.[activeGroupKey];
      }
      const groupKey = activeGroupKey;
      const groupCollapsed = activeGroupCollapsed;

      rows.push(
        <React.Fragment key={this.getUniqueRowIdentifier(element)}>
          {element.emptyKey ? (
            <Row>
              <div className={"rowCol"}>
                {questionChanges[0] !== "new" && (
                  <div className={"header-rows-cols text-start card-header"}>
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 text-start w-100 d-flex align-items-center justify-content-start lh-sm text-white"
                      onClick={(event) => {
                        event.preventDefault();
                        this.handleToggleRowsAndColumns(groupKey);
                      }}
                    >
                      <FontAwesomeIcon icon={groupCollapsed ? faChevronDown : faChevronUp} className="me-2" />
                      <span>
                        {intl.formatMessage({ id: `survey.version.compare.${element.emptyKey}` })}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
              <div className={"rowCol"}>
                {questionChanges[0] !== "deleted" && (
                  <div className={"header-rows-cols text-start card-header"}>
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 text-start w-100 d-flex align-items-center justify-content-start lh-sm text-white"
                      onClick={(event) => {
                        event.preventDefault();
                        this.handleToggleRowsAndColumns(groupKey);
                      }}
                    >
                      <FontAwesomeIcon icon={groupCollapsed ? faChevronDown : faChevronUp} className="me-2" />
                      <span>
                        {intl.formatMessage({ id: `survey.version.compare.${element.emptyKey}` })}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            </Row>
          ) : !groupCollapsed ? (
            <Row>
              <div className={"rowCol"}>
                {element.change[0] !== "new" && (
                  <div className={`card-header ${cellType}`}>
                    {element.dataTypeOld && (
                      <i className={element.dataTypeOld.charAt(0).toLowerCase() + element.dataTypeOld.slice(1)}></i>
                    )}
                    <Row className="g-1 align-items-center">
                      {hasCollapsibleChoices && (
                        <Col xs="auto" className="pe-1">
                          <Button
                            type="button"
                            variant="link"
                            className="p-0"
                            onClick={(event) => {
                              event.preventDefault();
                              this.handleToggleChoices(choicesGroupKey);
                            }}
                          >
                            <FontAwesomeIcon icon={choicesCollapsed ? faChevronDown : faChevronUp} />
                          </Button>
                        </Col>
                      )}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={this.generateTooltip(
                          sanitizeNoAllowedTags(element.titleOld) || sanitizeNoAllowedTags(element.nameOld)
                        )}
                      >
                        <Col>
                          {sanitizeNoAllowedTags(element.titleOld) || sanitizeNoAllowedTags(element.nameOld)}
                        </Col>
                      </OverlayTrigger>

                      <Col lg={5} className="text-end">
                        {!element.change ||
                        element.change.length === 0 ||
                        (element.change && element.change.length > 0 && element.change[0] !== "new")
                          ? element.keyOld
                          : ""}
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
              <div className={"rowCol"}>
                {element.change[0] !== "deleted" && (
                  <div className={`card-header ${cellType}`}>
                    {element.dataTypeNew && (
                      <i className={element.dataTypeNew.charAt(0).toLowerCase() + element.dataTypeNew.slice(1)}></i>
                    )}
                    <Row className="g-1 align-items-center">
                      {hasCollapsibleChoices && (
                        <Col xs="auto" className="pe-1">
                          <Button
                            type="button"
                            variant="link"
                            className="p-0"
                            onClick={(event) => {
                              event.preventDefault();
                              this.handleToggleChoices(choicesGroupKey);
                            }}
                          >
                            <FontAwesomeIcon icon={choicesCollapsed ? faChevronDown : faChevronUp} />
                          </Button>
                        </Col>
                      )}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={this.generateTooltip(
                          sanitizeNoAllowedTags(element.titleNew) || sanitizeNoAllowedTags(element.nameNew)
                        )}
                      >
                        <Col>
                          {sanitizeNoAllowedTags(element.titleNew) || sanitizeNoAllowedTags(element.nameNew)}
                        </Col>
                      </OverlayTrigger>

                      <Col lg={5} className="text-end">
                        {!element.change ||
                        element.change.length === 0 ||
                        (element.change && element.change.length > 0 && element.change[0] !== "deleted") ? (
                          <>
                            {!isLastVersionSelected ?
                              (
                                <>
                                  {element.keyNew}
                                </>
                              ) : (
                                <input
                                    type="text"
                                    value={element.keyNew}
                                    autoFocus={elementKey === this.state.focusedId ? true : false}
                                    onFocus={(e) => {
                                      if (this.state.focusedId !==elementKey) {
                                        this.setState({ focusedId: elementKey });
                                      }
                                    }}
                                    onChange={(e) => {
                                      this.setState({ focusedId: elementKey });
                                      changeQuestionField(
                                        element.pageName,
                                        element.questionName,
                                        "key",
                                        e.target.value,
                                        element.choiceName,
                                        element.choiceTarget,
                                        null,
                                        true
                                      );
                                    }}
                                />
                              )
                            }

                            {element.newKeyAlreadyUsed && this.duplicateKeysWarningIcon()}
                          </>
                        ) : (
                          ""
                        )}
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            </Row>
          ) : null}

          {!groupCollapsed &&
            hasChoices &&
            (!hasCollapsibleChoices || !choicesCollapsed) &&
            element.children.map((choice) => {
              const cellType = `cell-${choice.change[0] ? choice.change[0] : "no-change"}`;
              const choiceKey = this.getChoiceToggleIdentifier(choice);

              return (
                <Row key={this.getUniqueRowIdentifier(choice)}>
                  <div className={"rowCol"}>
                    {choice.change[0] !== "new" && (
                      <Row className={`choice card-header ${cellType}`}>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={this.generateTooltip(
                            sanitizeNoAllowedTags(choice.titleOld) || sanitizeNoAllowedTags(choice.nameOld)
                          )}
                        >
                          <Col lg={7}>
                            {sanitizeNoAllowedTags(choice.titleOld) || sanitizeNoAllowedTags(choice.nameOld)}
                          </Col>
                        </OverlayTrigger>

                        <Col lg={5} className="text-end">
                          {!choice.change ||
                          choice.change.length === 0 ||
                          (choice.change && choice.change.length > 0 && choice.change[0] !== "new")
                            ? choice.keyOld
                            : ""}
                        </Col>
                      </Row>
                    )}
                  </div>
                  {choice.change[0] !== "deleted" && (
                    <div className={"rowCol"}>
                      <Row className={`choice card-header  ${cellType}`}>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={this.generateTooltip(
                            sanitizeNoAllowedTags(choice.titleNew) || sanitizeNoAllowedTags(choice.nameNew)
                          )}
                        >
                          <Col lg={7}>
                            {sanitizeNoAllowedTags(choice.titleNew) || sanitizeNoAllowedTags(choice.nameNew)}
                          </Col>
                        </OverlayTrigger>

                        <Col lg={5} className="text-end">
                          {!choice.change ||
                          choice.change.length === 0 ||
                          (choice.change && choice.change.length > 0 && choice.change[0] !== "deleted") ? (
                            <>
                              {!isLastVersionSelected ?
                                (
                                  <>
                                    {choice.keyNew}
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={choice.keyNew}
                                    autoFocus={choiceKey === this.state.focusedId ? true : false}
                                    onFocus={(e) => {
                                      if (this.state.focusedId !== choiceKey) {
                                        this.setState({ focusedId: choiceKey });
                                      }
                                    }}
                                    onChange={(e) => {
                                      this.setState({ focusedId: choiceKey });                                
                                      changeQuestionField(
                                        choice.pageName,
                                        choice.questionName,
                                        "key",
                                        e.target.value,
                                        choice.choiceName,
                                        element.choiceTarget,
                                        choice.nameNew,
                                        true
                                      );
                                    }}
                                  />
                                )
                              }
                              {choice.newKeyAlreadyUsed && this.duplicateKeysWarningIcon()}
                            </>
                          ) : (
                            ""
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                </Row>
              );
            })}
        </React.Fragment>
      );
    });

    return rows;
  }

  renderDiffTableContent(comparison, rows) {
    comparison.forEach((row) => {
      if (row.typeIndex === 1) {
        this.renderQuestion(row, rows);
      } else {
        this.renderPage(row, rows);
      }
    });
  }

  getQuestionToggleIdentifier(question) {
    return `${this.getTextWithoutSpaces(question.pageName)}-${this.getTextWithoutSpaces(question.nameNew)}`;
  }

  getElementToggleIdentifier(element) {
    return `${this.getTextWithoutSpaces(element.pageName)}-${this.getTextWithoutSpaces(element.questionName)}-${this.getTextWithoutSpaces(element.nameNew)}`;
  }

  getChoiceToggleIdentifier(choice) {
    return `${this.getTextWithoutSpaces(choice.pageName)}-${this.getTextWithoutSpaces(choice.questionName)}-${this.getTextWithoutSpaces(choice.choiceName)}-${this.getTextWithoutSpaces(choice.nameNew)}`;
  }

  getRowsAndColumnsGroupIdentifier(element) {
    return `${this.getTextWithoutSpaces(element.pageName)}-${this.getTextWithoutSpaces(element.questionName)}-${this.getTextWithoutSpaces(element.choiceTarget)}-${this.getTextWithoutSpaces(element.emptyKey)}`;
  }

  getChoicesGroupIdentifier(element) {
    return `${this.getTextWithoutSpaces(element.pageName)}-${this.getTextWithoutSpaces(element.questionName)}-${this.getTextWithoutSpaces(element.choiceTarget)}-${this.getTextWithoutSpaces(element.nameNew || element.nameOld)}-choices`;
  }

  hasCollapsibleChoices(element) {
    const selectableTypes = ["checkbox", "dropdown", "radio"];
    return [element.dataTypeOld, element.dataTypeNew]
      .filter(Boolean)
      .some((type) => selectableTypes.includes(String(type).toLowerCase()));
  }

  getPageToggleIdentifier(page) {
    return `${this.getTextWithoutSpaces(page.nameNew || page.nameOld)}`;
  }

  getRowIdentifier(row) {
    return [
      row.type || "row",
      row.typeIndex,
      row.emptyKey,
      row.pageName,
      row.questionName,
      row.choiceName,
      row.choiceTarget,
      row.nameNew || row.nameOld
    ]
      .map((part) => this.getTextWithoutSpaces(part))
      .join("-");
  }

  getUniqueRowIdentifier(row) {
    const rowIdentifier = this.getRowIdentifier(row);
    const count = this.rowKeyCounts[rowIdentifier] || 0;
    this.rowKeyCounts[rowIdentifier] = count + 1;

    return `${rowIdentifier}-${count}`;
  }

  getTextWithoutSpaces(text) {
    if (text) {
      return String(text).replace(/\s+/g, "");
    } 

    return "";
  }

  tooltip(text) {
    return (
      <Tooltip id={"infoTooltip"} className={"programTooltip"}>
        {text}
      </Tooltip>
    );
  }
}

SurveyVersionComparisonTable.propTypes = {
  surveyComparison: PropTypes.array,
  latestSurvey: PropTypes.object,
  lastApprovedSurvey: PropTypes.object,
  currentIdentifier: PropTypes.string,
  currentVersion: PropTypes.number,
  isLastVersionSelected: PropTypes.bool,
  isPageExpanded: PropTypes.func,
  onTogglePage: PropTypes.func,
  isQuestionExpanded: PropTypes.func,
  onToggleQuestion: PropTypes.func,
};

export default injectIntl(SurveyVersionComparisonTable);
